import React, { useContext, useState } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import RemoveIcon from 'assets/icons/remove-icon'
import ConfirmIcon from 'assets/icons/confirm-icon'
import RejectIcon from 'assets/icons/reject-icon'
import AssessIcon from 'assets/icons/assess-icon'
import ReassessIcon from 'assets/icons/reassess-icon'
import PulsingNotification from 'features/PulsingNotification'
import { SiteContext } from 'providers/site'
import { defaultSiteProposalValidationResults } from 'constants/defaultSiteProposalValidationResults'
import { SITE_STATUS_DRAFT_ID, SITE_STATUS_NOMINATED_ID } from 'constants/siteStatuses'
import { ProfileContext } from 'providers/profile'
import { SECRETARIAT_ROLE_ID, BIRDLIFE_SECRETARIAT_ROLE_ID, INDIVIDUAL_ROLE_ID } from 'constants/roles'
import CancelIcon from 'assets/icons/cancel-icon16px'
import ActionsMenu from 'features/ActionMenu'
import { confirmEcoSystem, cancelEcosystemReassessmentRequest } from 'api/ecoSystem'
import Tooltip from 'components/Tooltip'
import Button from 'components/Button'
import { showErrorToast } from 'utils/toast'
import Modal from 'components/Modal'
import FlowRequestModal from 'components/FlowRequestModal'
import CalendarIcon from 'assets/icons/calendar-icon'
import DropdownIndicatorIconOpen from 'assets/icons/dropdown-indicator-opened24px'
import DropdownIndicatorIconClosed from 'assets/icons/dropdown-indicator-closed24px'
import { getActionMenuItems, getRejectReasonNames, getReassessmentActionId } from 'utils/ecosystemHeaderHelpers'
import {
  EcosystemActionMenuWrapper,
  EcosystemCalendarDateText, EcosystemCalendarDivider, EcosystemCalendarIconWrapper,
  EcosystemConfirmDeclineIcon,
  EcosystemHeaderIcon,
  EcosystemHeaderIconWrapper,
  EcosystemHeaderNavbarHalfSide,
  EcosystemHeaderNavbarIconWrapper,
  EcosystemHeaderNavbarWrapper,
  EcosystemHeaderTitle, EcosystemLastReassessmentCalendarWrapper, EcosystemToggleWrapper,
  EcoSystemValidationIcon, EcosystemValidationIconWrapper,
  EcosystemsCancelIconWrapper
} from './style'
import { formatDate } from 'utils/dateFormat'

const tooltipXOffset = 0
const tooltipYOffset = -16

const ReassessmentDate = ({ dateToShow }) => {
  if (dateToShow === '') {
    return null
  }

  return (
    <Tooltip
      style={{ zIndex: 1000 }}
      placement='top'
      trigger={['hover']}
      align={{ offset: [tooltipXOffset, tooltipYOffset] }}
      overlay={{ label: 'lastTimeReassessed' }}
    >
      <EcosystemLastReassessmentCalendarWrapper>
        <EcosystemCalendarIconWrapper>
          <CalendarIcon />
        </EcosystemCalendarIconWrapper>
        <EcosystemCalendarDateText>
          {dateToShow}
        </EcosystemCalendarDateText>
        <EcosystemCalendarDivider />
      </EcosystemLastReassessmentCalendarWrapper>
    </Tooltip>
  )
}
ReassessmentDate.propTypes = {
  dateToShow: PropTypes.string
}

const RemoveEcosystemButton = ({ item, openRemoveEcoSystemModal, isDraftAndEnabled, isReassessmentMode }) => {
  if (!isDraftAndEnabled) {
    return null
  }

  if (isReassessmentMode && item.lastReassessmentDate) {
    return null
  }

  return (
    <EcosystemHeaderNavbarIconWrapper
      delete={isReassessmentMode}
      data-test='remove-button'
      onClick={(e) => {
        e.stopPropagation()
        openRemoveEcoSystemModal(item)
      }}
    >
      <RemoveIcon />
    </EcosystemHeaderNavbarIconWrapper>
  )
}
RemoveEcosystemButton.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string,
    triggerReasonId: PropTypes.arrayOf(PropTypes.number),
    reassessmentFlag: PropTypes.bool,
    updatedAt: PropTypes.string,
    lastReassessmentDate: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
    isApprovedBySecretariat: PropTypes.bool
  }).isRequired,
  openRemoveEcoSystemModal: PropTypes.func.isRequired,
  isDraftAndEnabled: PropTypes.bool.isRequired,
  isReassessmentMode: PropTypes.bool.isRequired
}

const ecosystemItemPropType = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  name: PropTypes.string,
  triggerReasonId: PropTypes.arrayOf(PropTypes.number),
  reassessmentFlag: PropTypes.bool,
  updatedAt: PropTypes.string,
  lastReassessmentDate: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  isApprovedBySecretariat: PropTypes.bool
})

const HeaderActions = ({
  site,
  profile,
  item,
  isOpen,
  oldTriggers,
  newTriggers,
  toggleAccordion,
  openRemoveEcoSystemModal,
  fieldsToCompleteNumber,
  actionMenuItems,
  disabled,
  setEcosystemToCancel,
  setIsCancelOpen,
  dateToShow,
  reassessmentActionId,
  qulifyes
}) => {
  const isDraft = site.SiteStatusId === SITE_STATUS_DRAFT_ID
  const isNominatedSecretariat = site.SiteStatusId === SITE_STATUS_NOMINATED_ID && (profile.RoleId === SECRETARIAT_ROLE_ID || profile.RoleId === BIRDLIFE_SECRETARIAT_ROLE_ID)
  const isDraftAndEnabled = isDraft && !disabled

  const renderValidationIndicator = () => {
    if (fieldsToCompleteNumber.length === 0) {
      return null
    }

    return (
      <EcosystemValidationIconWrapper>
        <EcoSystemValidationIcon>
          <PulsingNotification number={fieldsToCompleteNumber.length} />
        </EcoSystemValidationIcon>
      </EcosystemValidationIconWrapper>
    )
  }

  const renderReassessmentActions = () => {
    return (
      <>
        <ReassessmentDate dateToShow={dateToShow} />
        <EcosystemConfirmDeclineIcon type={qulifyes ? 'confirmed' : 'rejected'}>
          {qulifyes ? (
            <Tooltip
              style={{ zIndex: 1000 }}
              placement='top'
              trigger={['hover']}
              align={{ offset: [tooltipXOffset, tooltipYOffset] }}
              overlay={{ label: 'qualifies' }}
            >
              <div>
                <ConfirmIcon />
              </div>
            </Tooltip>
          ) : (
            <Tooltip
              style={{ zIndex: 1000 }}
              placement='top'
              trigger={['hover']}
              align={{ offset: [tooltipXOffset, tooltipYOffset] }}
              overlay={{ label: 'nonQualifying', infoTooltip: getRejectReasonNames(item).join(', ') }}
            >
              <div>
                <RejectIcon />
              </div>
            </Tooltip>
          )}
        </EcosystemConfirmDeclineIcon>
        {isDraft ? (
          <>
            <Button
              isDisabled={isOpen}
              type={isOpen ? 'defaultDisabled' : ' '}
              height='32px'
              marginTop='0'
              onClick={toggleAccordion}
            >
              <ReassessIcon />
              <FormattedMessage id={reassessmentActionId} />
            </Button>
            {oldTriggers && (
              <EcosystemsCancelIconWrapper onClick={(e) => {
                e.stopPropagation()
                setEcosystemToCancel(item)
                setIsCancelOpen(true)
              }}>
                <CancelIcon />
              </EcosystemsCancelIconWrapper>
            )}
          </>
        ) : (
          <EcosystemToggleWrapper isActive={isOpen} onClick={toggleAccordion}>
            <FormattedMessage id='expand' />
            {isOpen ? <DropdownIndicatorIconOpen /> : <DropdownIndicatorIconClosed />}
          </EcosystemToggleWrapper>
        )}
        <RemoveEcosystemButton
          item={item}
          openRemoveEcoSystemModal={openRemoveEcoSystemModal}
          isDraftAndEnabled={isDraftAndEnabled}
          isReassessmentMode={true}
        />
      </>
    )
  }

  const renderStandardActions = () => {
    return (
      <>
        {isDraft ? (
          <Button
            height='32px'
            onClick={toggleAccordion}
            marginTop='0'
            isDisabled={isOpen}
            type={isOpen ? 'defaultDisabled' : ''}
          >
            <AssessIcon />
            <FormattedMessage id={reassessmentActionId} />
          </Button>
        ) : (
          <>
            <ReassessmentDate dateToShow={dateToShow} />
            <EcosystemToggleWrapper isActive={isOpen} onClick={toggleAccordion}>
              <FormattedMessage id='expand' />
              {isOpen ? <DropdownIndicatorIconOpen /> : <DropdownIndicatorIconClosed />}
            </EcosystemToggleWrapper>
          </>
        )}
        <RemoveEcosystemButton
          item={item}
          openRemoveEcoSystemModal={openRemoveEcoSystemModal}
          isDraftAndEnabled={isDraftAndEnabled}
          isReassessmentMode={false}
        />
        {isNominatedSecretariat && (
          <EcosystemActionMenuWrapper>
            <ActionsMenu table={false} dropdownItems={actionMenuItems} margin='0' />
          </EcosystemActionMenuWrapper>
        )}
      </>
    )
  }

  return (
    <>
      {site.reassessmentFlag ? renderReassessmentActions() : renderStandardActions()}
      {renderValidationIndicator()}
    </>
  )
}
HeaderActions.propTypes = {
  site: PropTypes.shape({
    SiteStatusId: PropTypes.number.isRequired,
    reassessmentFlag: PropTypes.bool
  }).isRequired,
  profile: PropTypes.shape({
    RoleId: PropTypes.number
  }).isRequired,
  item: ecosystemItemPropType.isRequired,
  isOpen: PropTypes.bool,
  oldTriggers: PropTypes.bool,
  newTriggers: PropTypes.bool,
  toggleAccordion: PropTypes.func.isRequired,
  openRemoveEcoSystemModal: PropTypes.func.isRequired,
  fieldsToCompleteNumber: PropTypes.arrayOf(PropTypes.bool).isRequired,
  actionMenuItems: PropTypes.arrayOf(PropTypes.shape({
    icon: PropTypes.node,
    title: PropTypes.string,
    onClick: PropTypes.func,
    isDestructive: PropTypes.bool
  })).isRequired,
  disabled: PropTypes.bool.isRequired,
  setEcosystemToCancel: PropTypes.func.isRequired,
  setIsCancelOpen: PropTypes.func.isRequired,
  dateToShow: PropTypes.string,
  reassessmentActionId: PropTypes.string.isRequired,
  qulifyes: PropTypes.bool
}

const EcoSystemAccordionHeader = ({ isOpen, item, openRemoveEcoSystemModal, getEcosystems, toggleAccordion, lastItem, newTriggers = false, oldTriggers = false }) => {
  const { site, siteProposalFieldValidation, isEditable } = useContext(SiteContext)
  const { profile } = useContext(ProfileContext)
  const isIndividualUser = profile.Role.id === INDIVIDUAL_ROLE_ID
  const dateToShow = item.reassessmentFlag ? formatDate(item.updatedAt) : formatDate(item.lastReassessmentDate)
  const [isCancelOpen, setIsCancelOpen] = useState(false)
  const [ecosystemToCancel, setEcosystemToCancel] = useState(null)

  const handleEcosystemAction = (isApproved) => {
    confirmEcoSystem(item.id, isApproved).then(() => {
      getEcosystems()
    })
  }

  const actionMenuItems = getActionMenuItems({
    isApprovedBySecretariat: item.isApprovedBySecretariat,
    onReject: () => handleEcosystemAction({ isApproved: false }),
    onConfirm: () => handleEcosystemAction({ isApproved: true })
  })

  const validationEcosystem = siteProposalFieldValidation.ecoSystem[item.id]
    ? siteProposalFieldValidation.ecoSystem[item.id]
    : defaultSiteProposalValidationResults.ecoSystem

  const fieldsToCompleteNumber = Object.values(validationEcosystem).filter((element) => {
    return element === false
  })

  const qulifyes = item.reassessmentFlag ? item.triggerReasonId?.includes(1) : true

  const cancelEcosystemsChanges = (ecosystem) => {
    if (!isIndividualUser) {
      showErrorToast('This action is not allowed.')
      return
    }
    cancelEcosystemReassessmentRequest({ siteId: site.historyId, ecosystemId: ecosystem.id, isCancelled: true }).then((data) => {
      if (data.error) {
        return showErrorToast(data.error)
      }
      setIsCancelOpen(false)
      setEcosystemToCancel(null)
      getEcosystems()
    })
  }

  const disabled = site.SiteStatusId !== SITE_STATUS_DRAFT_ID || (!isEditable && site.SiteStatusId === SITE_STATUS_DRAFT_ID) || !qulifyes
  const reassessmentActionId = getReassessmentActionId(oldTriggers, newTriggers)

  return (
    <>
      <EcosystemHeaderNavbarWrapper
        isOpen={isOpen}
        bottomBorder={isOpen || lastItem}
        data-test='ecosystem'
        onClick={() => { }}>
        <EcosystemHeaderNavbarHalfSide onClick={toggleAccordion}>
          <EcosystemHeaderIconWrapper>
            <EcosystemHeaderIcon isRejected={!qulifyes} />
          </EcosystemHeaderIconWrapper>
          <EcosystemHeaderTitle
            isRejected={!qulifyes}
            data-test='ecosystem-name'
            isReassess={item.lastReassessmentDate}
          >
            {item.name}
          </EcosystemHeaderTitle>
        </EcosystemHeaderNavbarHalfSide>
        <EcosystemHeaderNavbarHalfSide>
          <HeaderActions
            site={site}
            profile={profile}
            item={item}
            isOpen={isOpen}
            oldTriggers={oldTriggers}
            newTriggers={newTriggers}
            toggleAccordion={toggleAccordion}
            openRemoveEcoSystemModal={openRemoveEcoSystemModal}
            fieldsToCompleteNumber={fieldsToCompleteNumber}
            actionMenuItems={actionMenuItems}
            disabled={disabled}
            setEcosystemToCancel={setEcosystemToCancel}
            setIsCancelOpen={setIsCancelOpen}
            dateToShow={dateToShow}
            reassessmentActionId={reassessmentActionId}
            qulifyes={qulifyes}
          />
        </EcosystemHeaderNavbarHalfSide>
      </EcosystemHeaderNavbarWrapper>
      <Modal
        width='416px'
        isOpen={isCancelOpen}
        toggleModal={setIsCancelOpen}
        modalTitleId='cancelEcosystemReassessment'
      >
        <FlowRequestModal
          cancelHandler={() => {
            setIsCancelOpen(false)
            setEcosystemToCancel(null)
          }}
          confirmHandler={() => {
            cancelEcosystemsChanges(ecosystemToCancel)
          }}
          confirmBtnText='yesCancel'
          rejectBtnText='continueEditing'
          text={<FormattedMessage id='returnDiscardEcosystemChangesText' values={{ ecosystemName: <b>{ecosystemToCancel?.name}</b> }} />}
          item={ecosystemToCancel}
          approve={true}
        />
      </Modal>
    </>
  )
}

export default EcoSystemAccordionHeader

EcoSystemAccordionHeader.propTypes = {
  isOpen: PropTypes.bool,
  item: ecosystemItemPropType.isRequired,
  openRemoveEcoSystemModal: PropTypes.func.isRequired,
  getEcosystems: PropTypes.func.isRequired,
  toggleAccordion: PropTypes.func.isRequired,
  lastItem: PropTypes.bool,
  newTriggers: PropTypes.bool,
  oldTriggers: PropTypes.bool
}